import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import { ReactElement, useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Add, ViewModule, ViewList, Search } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { useNotifications } from 'src/v3/manage/hooks/use-notifications'
import { usePageTour } from 'src/v3/tours/hooks/use-page-tour'
import {
  fetchCourses,
  fetchLinkedServices,
  fetchCategories,
  fetchAllAccreditations,
  selectCoursesStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectSelectedLinkedService,
  selectCategoriesStatus,
  selectCourses,
  selectCategoriesAll,
  selectedLinkedServiceChanged,
  selectAccreditations,
  selectAccreditationsStatus,
} from '../create.slice'
import CourseMetrics from './components/course-metrics'
import CourseCard from './components/course-card'
import CourseLineItem from './components/course-line-item'
import { CREATE_COURSE_URLS } from '../constants'
import { deleteCourse } from '../utils/api'
import { LightweightCourseV5 } from './types'
import ConfirmDialog from './components/modals/confirm-dialog'

export default function CreateCourseV5(): ReactElement {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let { showSuccess, showError, NotificationComponent } = useNotifications()

  usePageTour('create-course')

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let activeLinkedService = useAppSelector(selectSelectedLinkedService)
  let sections = useAppSelector(selectCategoriesAll)
  let sectionsStatus = useAppSelector(selectCategoriesStatus)
  let accreditations = useAppSelector(selectAccreditations)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)

  let [linkedServiceFilter, setLinkedServiceFilter] = useState<string>('all')
  let [categoryFilter, setCategoryFilter] = useState<string>('all')
  let [statusFilter, setStatusFilter] = useState<string>('all')
  let [searchQuery, setSearchQuery] = useState<string>('')
  let [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  let [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  let [courseToDelete, setCourseToDelete] =
    useState<LightweightCourseV5 | null>(null)

  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(sectionsStatus, fetchCategories)
  useAutoFetch(accreditationsStatus, fetchAllAccreditations)

  useEffect(() => {
    if (activeLinkedService && activeLinkedService !== 'unselected') {
      setLinkedServiceFilter(activeLinkedService)
    }
  }, [activeLinkedService])

  let sectionNameById = useMemo(
    () => new Map(sections.map((s) => [s.id, s.name])),
    [sections],
  )

  let filteredSections = useMemo(
    () =>
      linkedServiceFilter === 'all'
        ? sections
        : sections.filter(
            (section) => section.linkedServiceID === linkedServiceFilter,
          ),
    [sections, linkedServiceFilter],
  )

  useEffect(() => {
    if (
      categoryFilter !== 'all' &&
      !filteredSections.some((s) => s.id === categoryFilter)
    ) {
      setCategoryFilter('all')
    }
  }, [linkedServiceFilter, filteredSections, categoryFilter])

  let filteredCourses = useMemo(
    () =>
      courses.filter((course) => {
        if (
          linkedServiceFilter &&
          linkedServiceFilter !== 'all' &&
          course.linkedServiceID !== linkedServiceFilter
        ) {
          return false
        }

        if (
          categoryFilter &&
          categoryFilter !== 'all' &&
          course.sectionID !== categoryFilter
        ) {
          return false
        }

        if (statusFilter === 'published' && course.published !== 'published') {
          return false
        }
        if (statusFilter === 'draft' && course.published === 'published') {
          return false
        }

        if (searchQuery) {
          let query = searchQuery.toLowerCase()
          let matchesName = course.name?.toLowerCase().includes(query)
          if (!matchesName) {
            return false
          }
        }

        return true
      }),
    [courses, linkedServiceFilter, categoryFilter, statusFilter, searchQuery],
  )

  let isFiltered =
    linkedServiceFilter !== 'all' ||
    categoryFilter !== 'all' ||
    statusFilter !== 'all' ||
    searchQuery !== ''

  let resetFilters = () => {
    setLinkedServiceFilter('all')
    setCategoryFilter('all')
    setStatusFilter('all')
    setSearchQuery('')
  }

  let handleDeleteClick = (course: LightweightCourseV5) => {
    setCourseToDelete(course)
    setDeleteDialogOpen(true)
  }

  let handleDeleteConfirm = async () => {
    if (!courseToDelete) return

    await deleteCourse(courseToDelete.id)
      .then(() => {
        dispatch(fetchCourses())
        setDeleteDialogOpen(false)
        setCourseToDelete(null)
        showSuccess(`Successfully deleted course ${courseToDelete?.name}`)
      })
      .catch((err: any) => {
        if (err?.response?.status === 409) {
          setDeleteDialogOpen(false)
          setCourseToDelete(null)
          showError(
            'This course cannot be deleted because students have activity associated with it.',
          )
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Error deleting course ${courseToDelete?.name}: ${courseToDelete?.id}. Error:`,
            err,
          )
          showError(`Error deleting course ${courseToDelete?.name}`)
        }
      })
  }

  let handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setCourseToDelete(null)
  }

  let isLoading =
    coursesStatus === 'pending' ||
    linkedServicesStatus === 'pending' ||
    sectionsStatus === 'pending' ||
    accreditationsStatus === 'pending'

  return (
    <>
      <Container maxWidth="xl" className="py-6">
        <Box className="mb-6" data-tour="create-course-index-header">
          <Typography variant="h4" component="h1" gutterBottom>
            Courses
          </Typography>
        </Box>
        <CourseMetrics
          courses={filteredCourses}
          isLoading={isLoading}
          accreditations={accreditations}
          accreditationsStatus={accreditationsStatus}
        />
        <Box className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <Button
            variant="contained"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate(CREATE_COURSE_URLS.CREATE)}
            className="mb-2 md:mb-0 basis-full md:basis-auto"
            data-tour="create-course-create-button"
          >
            Create Course
          </Button>

          <Box className="flex items-center justify-end gap-2">
            <Box role="group" aria-label="View mode">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode)
                  }
                }}
                size="small"
              >
                <ToggleButton value="grid" aria-label="grid view">
                  <ViewModule />
                </ToggleButton>
                <ToggleButton value="list" aria-label="list view">
                  <ViewList />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box role="status" aria-live="polite" aria-atomic="true">
              <Chip
                size="medium"
                label={`${filteredCourses.length} course${filteredCourses.length === 1 ? '' : 's'}`}
                color="primary"
              />
            </Box>

            {isFiltered && (
              <Button
                size="medium"
                variant="outlined"
                color="secondary"
                onClick={resetFilters}
                className="h-8 whitespace-nowrap"
              >
                Reset Filters
              </Button>
            )}
          </Box>
        </Box>
        <Box className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-4">
          <FormControl sx={{ backgroundColor: 'background.paperDark' }}>
            <InputLabel id="linked-service-filter">Linked Service</InputLabel>
            <Select
              labelId="linked-service-filter"
              label="Linked Service"
              value={linkedServiceFilter}
              onChange={(e) => {
                setLinkedServiceFilter(e.target.value)
                dispatch(selectedLinkedServiceChanged(e.target.value))
              }}
              size="medium"
            >
              <MenuItem value="all">
                <em>All Linked Services</em>
              </MenuItem>
              {linkedServices.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ backgroundColor: 'background.paperDark' }}>
            <InputLabel id="category-filter">Category</InputLabel>
            <Select
              labelId="category-filter"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              size="medium"
            >
              <MenuItem value="all">
                <em>All Categories</em>
              </MenuItem>
              {filteredSections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ backgroundColor: 'background.paperDark' }}>
            <InputLabel id="status-filter">Status</InputLabel>
            <Select
              labelId="status-filter"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="medium"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Search"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="medium"
            sx={{ backgroundColor: 'background.paperDark' }}
            slotProps={{
              input: {
                startAdornment: <Search className="mr-2" aria-hidden="true" />,
              },
            }}
          />
        </Box>
        {isLoading ? (
          <Box className="py-16 text-center" role="status" aria-live="polite">
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Loading courses...
            </Typography>
          </Box>
        ) : filteredCourses.length === 0 ? (
          <Box className="py-16 text-center">
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontSize: '1rem',
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              {isFiltered
                ? 'No courses match your filters'
                : 'No courses found'}
            </Typography>
            {isFiltered && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={resetFilters}
                className="mt-4"
              >
                Clear Filters
              </Button>
            )}
          </Box>
        ) : viewMode === 'grid' ? (
          <Box className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                categoryName={sectionNameById.get(course.sectionID) ?? ''}
                onDelete={handleDeleteClick}
              />
            ))}
          </Box>
        ) : (
          <Box className="flex flex-col gap-4">
            {filteredCourses.map((course) => (
              <CourseLineItem
                key={course.id}
                course={course}
                categoryName={sectionNameById.get(course.sectionID) ?? ''}
                onDelete={handleDeleteClick}
              />
            ))}
          </Box>
        )}
        <ConfirmDialog
          isOpen={deleteDialogOpen}
          title="Delete Course"
          message={`Are you sure you want to delete "${courseToDelete?.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
        />
      </Container>
      {NotificationComponent}
    </>
  )
}
